import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingCart, Star, Mail, Phone, ChevronDown, UserCircle, BookHeart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useSettings, resolveImageUrl } from '../context/SettingsContext';
import { useGoogleRating } from '../hooks/useGoogleRating';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Button } from './ui/button';
import CartSheet from './CartSheet';
import ResponsiveImage from './ResponsiveImage';

const MobileNavItem = ({ item, isActive, onNavigate }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const baseCls = `block px-5 py-4 rounded-2xl text-lg font-bold transition-all ${
    isActive ? 'bg-sky-500 text-white shadow' : 'bg-sky-50 text-sky-700'
  }`;
  if (!hasChildren) {
    return item.external ? (
      <a href={item.href} target="_blank" rel="noreferrer" onClick={onNavigate} className={baseCls}>{item.name}</a>
    ) : (
      <Link to={item.href} onClick={onNavigate} className={baseCls}>{item.name}</Link>
    );
  }
  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className={`${baseCls} w-full flex items-center justify-between`}
      >
        <span>{item.name}</span>
        <span className={`transition-transform ${expanded ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {expanded && (
        <div className="ml-3 mt-2 space-y-1.5">
          <Link to={item.href} onClick={onNavigate} className="block px-4 py-2.5 rounded-xl text-base font-semibold text-sky-700 bg-sky-50/60 hover:bg-sky-100">
            ➜ {item.name} (Übersicht)
          </Link>
          {item.children.map((c) => (
            c.external ? (
              <a key={c.name} href={c.href} target="_blank" rel="noreferrer" onClick={onNavigate} className="block px-4 py-2.5 rounded-xl text-base font-semibold text-sky-700 hover:bg-sky-50">{c.name}</a>
            ) : (
              <Link key={c.name} to={c.href} onClick={onNavigate} className="block px-4 py-2.5 rounded-xl text-base font-semibold text-sky-700 hover:bg-sky-50">{c.name}</Link>
            )
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { cart } = useCart();
  const { settings } = useSettings();
  const { user: customerUser } = useCustomerAuth();

  useEffect(() => {
    // Hysterese verhindert Flackern rund um die Schwelle: einklappen bei
    // scrollY > 120, wieder ausklappen erst wenn scrollY < 20 (Toggle-Loop
    // durch Content-Shift ausgeschlossen). Passiver Listener + requestAnimationFrame
    // vermeidet Layout-Thrashing bei schnellem Scroll.
    let ticking = false;
    let last = false;
    const apply = () => {
      const y = window.scrollY;
      const next = last ? y > 20 : y > 120;
      if (next !== last) {
        last = next;
        setScrolled(next);
      }
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Allow external triggers (e.g. mobile sticky CTA "Menü" button) to open menu
  useEffect(() => {
    const open = () => setMobileMenuOpen(true);
    window.addEventListener('open-mobile-menu', open);
    return () => window.removeEventListener('open-mobile-menu', open);
  }, []);

  // Broadcast open/close so the sticky bottom bar can hide itself while the menu is open.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent(mobileMenuOpen ? 'mobile-menu-open' : 'mobile-menu-close'));
  }, [mobileMenuOpen]);

  const settingsNav = settings?.navigation;
  const [customPages, setCustomPages] = useState([]);
  const [variants, setVariants] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pages`)
      .then((r) => r.ok ? r.json() : [])
      .then((pages) => setCustomPages(Array.isArray(pages) ? pages.filter((p) => p.in_navigation) : []))
      .catch(() => setCustomPages([]));
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/home-variants`)
      .then((r) => r.ok ? r.json() : [])
      .then((vs) => setVariants(Array.isArray(vs) ? vs.filter((v) => v.in_navigation) : []))
      .catch(() => setVariants([]));
  }, []);

  const navigation = useMemo(() => {
    const base = (Array.isArray(settingsNav) && settingsNav.length > 0)
      ? settingsNav.map((n) => ({
          name: n.label, href: n.href, order: n.order || 0,
          external: !!n.external,
          children: (n.children || []).map((c) => ({ name: c.label, href: c.href, external: !!c.external })),
        }))
      : [
          { name: 'Home', href: '/', order: 1, children: [] },
          { name: 'Unsere Hüpfburgen', href: '/produkte', order: 2, children: [] },
          { name: 'Kinderbetreuung', href: '/kinderbetreuung-service', order: 3, children: [] },
          { name: 'Mietanfrage', href: '/mietanfrage', order: 6, children: [] },
        ];
    const custom = customPages.map((p) => ({
      name: p.nav_label || p.title,
      href: `/${p.slug}`,
      order: p.nav_order ?? 100,
      external: false,
      children: [],
    }));
    const lp = variants.map((v) => ({
      name: v.nav_label || v.title,
      href: `/${v.slug}`,
      order: v.nav_order ?? 100,
      external: false,
      children: [],
    }));
    return [...base, ...custom, ...lp].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [settingsNav, customPages, variants]);

  const isActive = (path) => location.pathname === path;
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const logoUrl = settings?.brand?.logo_url;
  const siteName = settings?.brand?.site_name || 'Hüpfburgen Verleih';
  const contact = settings?.contact || {};
  const topbar = settings?.topbar || {};
  const googleRating = useGoogleRating();
  // Live Google data overrides static values when available
  const ratingValue = googleRating.rating != null
    ? googleRating.rating.toFixed(1).replace('.', ',')
    : topbar.rating_value;
  const ratingCount = googleRating.total != null
    ? `${googleRating.total} Bewertungen`
    : topbar.rating_count;
  const showRating = Boolean(topbar.rating_text || ratingValue || ratingCount);

  return (
    <header className="md:sticky md:top-0 md:z-50 relative z-40">
      <div className="bg-[#005898] transition-all duration-300">
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-300 ${scrolled ? 'pt-1 pb-1' : 'pt-3 pb-2'}`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <Link to="/" className="flex items-center" data-testid="header-logo-link">
              {logoUrl ? (
                <ResponsiveImage
                  src={logoUrl}
                  alt={siteName}
                  className={`w-auto drop-shadow-md transition-all duration-300 ${scrolled ? 'h-12 md:h-14' : 'h-20 md:h-28 lg:h-32'}`}
                  priority
                  blurUp={false}
                  sizes="(min-width: 1024px) 320px, 200px"
                />
              ) : (
                <span className="text-2xl font-bold text-white">{siteName}</span>
              )}
            </Link>

            <div className={`flex-1 md:px-4 transition-all duration-300 ${scrolled ? 'opacity-0 max-h-0 overflow-hidden pointer-events-none' : 'opacity-100'}`}>
              {/* Phase 128.56 — Beide Zeilen exakt gleich breit: aussen `w-fit`
                  passt die Breite an die breitere (untere) Zeile an, Zeile 1
                  bekommt `w-full` + `justify-between` und spreizt Email/Telefon
                  auf denselben Abstand wie Kundenmomente/Bewertung. */}
              <div className="hidden md:flex flex-col items-end gap-2 w-fit ml-auto md:mx-auto">
                <div className="flex items-center justify-between w-full text-white text-sm md:text-base gap-6">
                  {contact.email && (
                    <a href={`mailto:${contact.email}`} className="flex items-center space-x-2 hover:text-yellow-300 transition-colors">
                      <Mail className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                      <span className="break-all">{contact.email}</span>
                    </a>
                  )}
                  {contact.phone && (
                    <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="flex items-center space-x-2 hover:text-yellow-300 transition-colors">
                      <Phone className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                      <span>{contact.phone}</span>
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to="/kundenmomente"
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-md border border-emerald-300/60 transition-colors font-semibold text-sm"
                    data-testid="header-guestbook-badge"
                    title="Zu den Kundenmomenten"
                  >
                    <BookHeart className="h-4 w-4" />
                    <span>Kundenmomente</span>
                  </Link>
                  {showRating && (
                  <Link
                    to="/referenzen"
                    className="inline-flex items-center gap-2.5 px-3 py-1 rounded-full bg-white/95 backdrop-blur-sm shadow-md border border-yellow-200 hover:bg-white transition-colors"
                    data-testid="header-reviews-badge"
                  >
                    <span className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </span>
                    {ratingValue && (
                      <span className="font-bold text-neutral-900 text-sm tabular-nums">{ratingValue}</span>
                    )}
                    {googleRating.total != null ? (
                      <span className="text-sm text-neutral-600">
                        – über <b className="text-neutral-900">{googleRating.total}</b> Familien empfehlen uns
                      </span>
                    ) : ratingCount ? (
                      <span className="text-sm text-neutral-600">({ratingCount})</span>
                    ) : null}
                  </Link>
                  )}
                </div>
              </div>

              {/* Mobile-Variante (unter md): kompakte Stacked-Anordnung */}
              <div className="md:hidden flex flex-col items-end gap-1 text-white text-sm">
                {contact.email && (
                  <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-yellow-300 transition-colors">
                    <Mail className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                    <span className="break-all">{contact.email}</span>
                  </a>
                )}
                {contact.phone && (
                  <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-yellow-300 transition-colors">
                    <Phone className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                    <span>{contact.phone}</span>
                  </a>
                )}
                <div className="flex items-center gap-2 justify-end mt-1">
                  <Link
                    to="/kundenmomente"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-md border border-emerald-300/60 font-semibold text-xs"
                    title="Zu den Kundenmomenten"
                  >
                    <BookHeart className="h-3.5 w-3.5" />
                    <span>Kundenmomente</span>
                  </Link>
                  {showRating && (
                    <Link to="/referenzen" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/95 shadow-md border border-yellow-200 text-xs">
                      <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                      {ratingValue && <span className="font-bold text-neutral-900 tabular-nums">{ratingValue}</span>}
                      {googleRating.total != null && <span className="text-neutral-600">({googleRating.total})</span>}
                    </Link>
                  )}
                </div>
              </div>
            </div>

            <div className="hidden md:flex items-center space-x-3">
              <Button
                className={`hidden md:inline-flex bg-yellow-400 hover:bg-yellow-500 text-sky-900 font-bold rounded-full shadow-md lift-on-hover transition-all duration-300 ${scrolled ? 'px-5 py-4 text-base' : 'px-7 py-6 text-base'}`}
                onClick={() => navigate('/mietanfrage')}
                data-testid="header-cta-button"
              >
                {topbar.cta_text || 'Mietanfrage'}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate(customerUser ? '/mein-konto' : '/login')}
                className="hidden md:inline-flex rounded-full bg-white hover:bg-sky-50 border-0 lift-on-hover h-12 w-12"
                data-testid="header-account-button"
                title={customerUser ? 'Mein Kundenkonto' : 'Anmelden'}
              >
                {customerUser?.picture ? (
                  <img src={customerUser.picture} alt={customerUser.name} className="h-9 w-9 rounded-full object-cover" />
                ) : (
                  <UserCircle className="h-6 w-6 text-sky-700" />
                )}
              </Button>
              <CartSheet>
                <Button
                  variant="outline"
                  size="icon"
                  className="hidden md:inline-flex relative rounded-full bg-white hover:bg-sky-50 border-0 lift-on-hover h-12 w-12"
                  data-testid="cart-button"
                >
                  <ShoppingCart className="h-6 w-6 text-sky-700" />
                  {totalItems > 0 && (
                    <span className="absolute -top-1 -right-1 bg-yellow-400 text-sky-900 text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                      {totalItems}
                    </span>
                  )}
                </Button>
              </CartSheet>
            </div>
          </div>
        </div>

        {/* Pill Navigation (Desktop) */}
        <div className={`hidden md:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-300 ${scrolled ? 'pb-2 pt-0' : 'pb-4 pt-1'}`}>
          <nav className={`pill-nav flex items-center justify-center flex-wrap gap-1 transition-all duration-300 ${scrolled ? 'py-0.5' : ''}`}>
            {navigation.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const cls = `rounded-full font-semibold transition-all ${
                scrolled ? 'px-4 py-1.5 text-base' : 'px-5 py-2.5 text-base'
              } ${isActive(item.href) ? 'bg-sky-500 text-white shadow' : 'text-sky-700 hover:bg-sky-50'}`;
              if (hasChildren) {
                return (
                  <div key={item.name} className="relative group">
                    <Link to={item.href} className={`${cls} inline-flex items-center gap-1`} data-testid={`nav-link-${item.name.toLowerCase().replace(/[\s/]/g, '-')}`}>
                      {item.name}
                      <ChevronDown className="h-3 w-3" />
                    </Link>
                    <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-150 absolute left-0 top-full pt-2 z-50 min-w-[240px]">
                      <div className="bg-white rounded-2xl shadow-xl border border-sky-100 py-2">
                        {item.children.map((c) => (
                          c.external ? (
                            <a key={c.name} href={c.href} target="_blank" rel="noreferrer" className="block px-4 py-2.5 text-base text-sky-700 hover:bg-sky-50">{c.name}</a>
                          ) : (
                            <Link key={c.name} to={c.href} className="block px-4 py-2.5 text-base text-sky-700 hover:bg-sky-50">{c.name}</Link>
                          )
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              if (item.external) {
                return <a key={item.name} href={item.href} target="_blank" rel="noreferrer" className={cls}>{item.name}</a>;
              }
              return <Link key={item.name} to={item.href} className={cls} data-testid={`nav-link-${item.name.toLowerCase().replace(/[\s/]/g, '-')}`}>{item.name}</Link>;
            })}
          </nav>
        </div>
      </div>

      {/* Mobile-only Action Bar: Burger + Mietanfrage + Cart */}
      <div className="md:hidden bg-[#005898] px-4 py-3 flex items-center justify-between gap-3" data-testid="mobile-action-bar">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-button"
          className="bg-white text-[#005898] hover:bg-sky-50 rounded-2xl h-11 w-11 border-2 border-white flex-shrink-0"
        >
          {mobileMenuOpen ? <X className="!h-6 !w-6" /> : <Menu className="!h-6 !w-6" />}
        </Button>
        <Button
          className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-sky-900 font-bold rounded-full py-3 text-base"
          onClick={() => navigate('/mietanfrage')}
          data-testid="header-cta-mobile"
        >
          {topbar.cta_text || 'Mietanfrage'}
        </Button>
        <CartSheet>
          <Button
            variant="outline"
            size="icon"
            className="relative rounded-2xl h-11 w-11 bg-white hover:bg-sky-50 border-2 border-white flex-shrink-0"
            data-testid="cart-button-mobile"
          >
            <ShoppingCart className="!h-5 !w-5 text-[#005898]" />
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 bg-yellow-400 text-sky-900 text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                {totalItems}
              </span>
            )}
          </Button>
        </CartSheet>
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate(customerUser ? '/mein-konto' : '/login')}
          className="rounded-2xl h-11 w-11 bg-white hover:bg-sky-50 border-2 border-white flex-shrink-0 p-0 overflow-hidden"
          data-testid="header-account-button-mobile"
          title={customerUser ? 'Mein Kundenkonto' : 'Anmelden'}
        >
          {customerUser?.picture ? (
            <img src={customerUser.picture} alt={customerUser.name} className="h-full w-full object-cover rounded-2xl" />
          ) : (
            <UserCircle className="!h-5 !w-5 text-[#005898]" />
          )}
        </Button>
      </div>

      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-[60] animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-menu-backdrop"
          />
          {/* Drawer slides up from bottom */}
          <div
            className="md:hidden fixed inset-x-0 bottom-0 z-[70] bg-white pt-3 px-4 pb-6 space-y-2 shadow-2xl max-h-[85vh] overflow-y-auto rounded-t-3xl animate-slide-up"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 56px)' }}
            data-testid="mobile-menu"
          >
            {/* Grabber */}
            <div className="flex justify-center mb-1">
              <div className="h-1.5 w-12 rounded-full bg-neutral-200" />
            </div>
            <div className="flex justify-between items-center mb-3">
              <span className="text-base font-bold text-sky-700">Menü</span>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-full bg-sky-50 hover:bg-sky-100 h-9 w-9 flex items-center justify-center text-sky-700"
                aria-label="Menü schließen"
                data-testid="mobile-menu-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Account row */}
            <button
              type="button"
              onClick={() => { setMobileMenuOpen(false); navigate(customerUser ? '/mein-konto' : '/login'); }}
              className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 mb-3 transition-colors ${customerUser ? 'bg-gradient-to-r from-sky-50 to-yellow-50 hover:from-sky-100' : 'bg-sky-50 hover:bg-sky-100'}`}
              data-testid="mobile-account-button"
            >
              {customerUser?.picture ? (
                <img src={customerUser.picture} alt={customerUser.name} className="h-10 w-10 rounded-full object-cover border-2 border-sky-200" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-sky-600 text-white flex items-center justify-center">
                  <UserCircle className="h-6 w-6" />
                </div>
              )}
              <div className="flex-1 text-left">
                <div className="font-bold text-sky-900">{customerUser ? customerUser.name || 'Mein Konto' : 'Anmelden / Registrieren'}</div>
                <div className="text-xs text-neutral-500 truncate">{customerUser ? customerUser.email : 'Buchungen, Bonuskarte & mehr'}</div>
              </div>
              <span className="text-sky-700 text-sm font-semibold">{customerUser ? 'Öffnen →' : 'Login →'}</span>
            </button>
            {navigation.map((item) => (
              <MobileNavItem
                key={item.name}
                item={item}
                isActive={isActive(item.href)}
                onNavigate={() => setMobileMenuOpen(false)}
              />
            ))}
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
